import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/auth.js'
import { api } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import PlaceInput from '../components/shared/PlaceInput.jsx'

/**
 * Three-step post-signup family wizard.
 *   1. About you   — self_person + default tree
 *   2. Parents     — mother / father, both skippable
 *   3. Grandparents — four slots, only shown for parents that exist
 *
 * Submit calls POST /api/onboarding/family which is a single transaction.
 * Skip-for-now sets onboarding_dismissed_at so we don't auto-redirect on
 * the next login (the dashboard banner picks up from there).
 */

const EMPTY_SLOT = {
  first_name: '', last_name: '', birth_year: '', birth_place: '',
  // Set when the user picks a dedup-search match. When set, the form
  // fields are hidden and the slot is sent to the API as a link rather
  // than a fresh create.
  existing_person_id: null,
  linked_label: null,
}

function makeInitialData(seed) {
  // Pre-fill self.first_name + self.last_name from the registration "name"
  // field. Most users register with "First Last" — split on the first
  // space, copy first chunk into first_name, rest into last_name.
  const fullName = (seed?.name || '').trim()
  let first = '', last = ''
  if (fullName) {
    const i = fullName.indexOf(' ')
    if (i === -1) { first = fullName }
    else { first = fullName.slice(0, i); last = fullName.slice(i + 1) }
  }
  return {
    self:          { ...EMPTY_SLOT, first_name: first, last_name: last },
    mother:        { ...EMPTY_SLOT },
    father:        { ...EMPTY_SLOT },
    mother_mother: { ...EMPTY_SLOT },
    mother_father: { ...EMPTY_SLOT },
    father_mother: { ...EMPTY_SLOT },
    father_father: { ...EMPTY_SLOT },
  }
}

function slotHasName(slot) {
  if (!slot) return false
  if (slot.existing_person_id) return true
  return !!(slot.first_name?.trim() || slot.last_name?.trim())
}

function formatYear(d) {
  if (!d) return null
  const m = String(d).match(/(\d{4})/)
  return m ? m[1] : null
}

function formatMatchLabel(match) {
  const name = [match.first_name, match.last_name].filter(Boolean).join(' ').trim()
  const by = formatYear(match.birth_date) || match.birth_year || null
  const dy = formatYear(match.death_date) || match.death_year || null
  const years = by ? (dy ? `${by} – ${dy}` : `b. ${by}`) : null
  const place = match.birth_place || null
  const tail = [years, place].filter(Boolean).join(', ')
  return tail ? `${name} (${tail})` : (name || 'Existing person')
}

export default function OnboardingPage() {
  usePageTitle('Welcome to Genealogic')
  const navigate = useNavigate()
  const { user, refresh } = useAuthStore()

  const [step, setStep] = useState(0)
  const [data, setData] = useState(() => makeInitialData(user))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Self-eject — only when re-running the wizard would DUPLICATE the user
  // (they already completed it, or already have a linked self-person; step 0
  // creates a fresh self-person + tree, so that's the real footgun).
  //
  // Crucially we do NOT eject on `onboarding_dismissed_at` or `has_trees`:
  // users who skipped onboarding, or made a tree manually, reach this page
  // BY EXPLICITLY clicking "Open the wizard" / "Add your family" — bouncing
  // them straight back out was the "wizard opens and immediately closes" bug.
  useEffect(() => {
    let cancelled = false
    refresh().then(() => {
      if (cancelled) return
      const fresh = useAuthStore.getState().user
      if (!fresh) return
      if (fresh.onboarding_completed_at || fresh.self_person_id) {
        navigate('/', { replace: true })
      }
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const motherProvided = slotHasName(data.mother)
  const fatherProvided = slotHasName(data.father)

  const personCount = useMemo(() => {
    let n = 1  // self
    if (motherProvided) n++
    if (fatherProvided) n++
    if (motherProvided && slotHasName(data.mother_mother)) n++
    if (motherProvided && slotHasName(data.mother_father)) n++
    if (fatherProvided && slotHasName(data.father_mother)) n++
    if (fatherProvided && slotHasName(data.father_father)) n++
    return n
  }, [data, motherProvided, fatherProvided])

  function update(slotKey, field, value) {
    setData(d => ({ ...d, [slotKey]: { ...d[slotKey], [field]: value } }))
  }

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    try {
      const res = await api.post('/onboarding/family', data)
      // Refresh /auth/me so onboarding_completed_at + self_person_id land
      // in the local store before we navigate to the focal person.
      await refresh()
      navigate(`/tree/${res.tree_id}/person/${res.self_person_id}`)
    } catch (err) {
      setError(err.message || 'Could not save your family. Please try again.')
      setSubmitting(false)
    }
  }

  async function handleSkip() {
    setSubmitting(true)
    // Optimistic local update so OnboardingGate doesn't bounce us back
    // before the /me round-trip lands. Worst case: if the API call fails
    // and the page is reloaded, we end up here again — but the user
    // still got out cleanly.
    const setUser = useAuthStore.getState().setUser || ((u) => {
      // Fallback when auth store hasn't been extended yet.
      try { localStorage.setItem('gl_user', JSON.stringify(u)) } catch {}
      useAuthStore.setState({ user: u })
    })
    if (user) setUser({ ...user, onboarding_dismissed_at: new Date().toISOString() })
    try {
      await api.post('/onboarding/dismiss', {})
      await refresh()
    } catch {
      // Swallow — local state already reflects the dismissal.
    }
    navigate('/', { replace: true })
  }

  const stepValid = step === 0 ? !!data.self.first_name.trim() : true

  return (
    <div className="auth-page onboarding-page">
      <div className="auth-card onboarding-card">
        <span className="auth-logo">Genealogic</span>
        <p className="auth-tagline">
          {step === 0 && 'Tell us about yourself. This will be the start of your family tree.'}
          {step === 1 && "Add your parents. You can skip the ones you'd rather fill in later."}
          {step === 2 && 'Add your grandparents. Skip the ones you don\'t know — you can always add them later.'}
        </p>

        <div className="onboarding-progress" aria-label={`Step ${step + 1} of 3`}>
          {[0, 1, 2].map(i => (
            <div key={i} className={`onboarding-progress__dot${i === step ? ' is-active' : ''}${i < step ? ' is-done' : ''}`} />
          ))}
        </div>

        {step === 0 && (
          <SlotForm
            slot={data.self}
            onChange={(field, value) => update('self', field, value)}
            firstNameRequired
            firstNamePlaceholder="Jan"
            lastNamePlaceholder="de Vries"
          />
        )}

        {step === 1 && (
          <div className="onboarding-pair">
            <ParentSlot
              label="Your mother"
              slot={data.mother}
              onChange={(field, value) => update('mother', field, value)}
            />
            <ParentSlot
              label="Your father"
              slot={data.father}
              onChange={(field, value) => update('father', field, value)}
            />
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-grandparents">
            {motherProvided ? (
              <div className="onboarding-grandparents__group">
                <h4>Your mother's parents</h4>
                <div className="onboarding-pair">
                  <ParentSlot
                    label="Her mother"
                    slot={data.mother_mother}
                    onChange={(field, value) => update('mother_mother', field, value)}
                  />
                  <ParentSlot
                    label="Her father"
                    slot={data.mother_father}
                    onChange={(field, value) => update('mother_father', field, value)}
                  />
                </div>
              </div>
            ) : (
              <p className="onboarding-skipped">
                You skipped your mother — her parents will be skipped too. You can add them later from the tree.
              </p>
            )}

            {fatherProvided ? (
              <div className="onboarding-grandparents__group">
                <h4>Your father's parents</h4>
                <div className="onboarding-pair">
                  <ParentSlot
                    label="His mother"
                    slot={data.father_mother}
                    onChange={(field, value) => update('father_mother', field, value)}
                  />
                  <ParentSlot
                    label="His father"
                    slot={data.father_father}
                    onChange={(field, value) => update('father_father', field, value)}
                  />
                </div>
              </div>
            ) : (
              <p className="onboarding-skipped">
                You skipped your father — his parents will be skipped too. You can add them later from the tree.
              </p>
            )}
          </div>
        )}

        {error && <p className="error">{error}</p>}

        <div className="onboarding-actions">
          {step > 0 && (
            <button type="button" className="btn btn-ghost" onClick={() => setStep(s => s - 1)} disabled={submitting}>
              Back
            </button>
          )}
          {step < 2 && (
            <button
              type="button"
              className="btn btn-gold"
              onClick={() => setStep(s => s + 1)}
              disabled={!stepValid || submitting}
            >
              Continue
            </button>
          )}
          {step === 2 && (
            <button
              type="button"
              className="btn btn-gold"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Saving…' : `Save ${personCount} ${personCount === 1 ? 'person' : 'people'}`}
            </button>
          )}
        </div>

        <p className="auth-switch">
          <button type="button" className="link-btn" onClick={handleSkip} disabled={submitting}>
            I'll do this later
          </button>
        </p>
      </div>
    </div>
  )
}

function SlotForm({ slot, onChange, firstNameRequired, firstNamePlaceholder, lastNamePlaceholder }) {
  return (
    <div className="onboarding-form">
      <label>
        First name {firstNameRequired ? <span className="onboarding-required">*</span> : null}
        <input
          type="text"
          value={slot.first_name}
          onChange={e => onChange('first_name', e.target.value)}
          placeholder={firstNamePlaceholder || ''}
          required={firstNameRequired}
        />
      </label>
      <label>
        Last name
        <input
          type="text"
          value={slot.last_name}
          onChange={e => onChange('last_name', e.target.value)}
          placeholder={lastNamePlaceholder || ''}
        />
      </label>
      <label>
        Birth year
        <input
          type="number"
          inputMode="numeric"
          min="1500"
          max={new Date().getFullYear()}
          value={slot.birth_year}
          onChange={e => onChange('birth_year', e.target.value)}
          placeholder="1955"
        />
      </label>
      <label>
        Birth place
        <PlaceInput
          value={slot.birth_place}
          onChange={v => onChange('birth_place', v)}
          placeholder="Rotterdam"
          className=""
        />
      </label>
    </div>
  )
}

function ParentSlot({ label, slot, onChange }) {
  return (
    <div className="onboarding-slot">
      <h5>{label}</h5>
      {slot.existing_person_id ? (
        <div className="onboarding-link">
          <div className="onboarding-link__body">
            <span className="onboarding-link__label">Linked to</span>
            <span className="onboarding-link__name">{slot.linked_label || 'Existing person'}</span>
          </div>
          <button
            type="button"
            className="onboarding-link__unlink"
            onClick={() => {
              onChange('existing_person_id', null)
              onChange('linked_label', null)
            }}
            aria-label="Unlink"
          >×</button>
        </div>
      ) : (
        <>
          <SlotForm slot={slot} onChange={onChange} />
          <DedupMatches slot={slot} onPick={(match) => {
            onChange('existing_person_id', match.id)
            onChange('linked_label', formatMatchLabel(match))
          }} />
        </>
      )}
    </div>
  )
}

// DedupMatches — debounced lookup against /api/persons/dedup-search.
// Fires when the user has typed at least 2 chars in last_name. Shows a
// compact list of likely matches; clicking one links the slot to that
// existing person instead of creating a new one. The "mine" bucket
// (persons in the user's own trees) is the highest-value signal here —
// it's how a user with two trees avoids creating a duplicate of their
// own grandfather.
function DedupMatches({ slot, onPick }) {
  const [matches, setMatches] = useState([])
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const last = (slot.last_name || '').trim()
    if (last.length < 2) {
      setMatches([])
      return
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const params = new URLSearchParams()
        params.set('last_name', last)
        if (slot.first_name?.trim()) params.set('first_name', slot.first_name.trim())
        if (slot.birth_year) params.set('birth_year', slot.birth_year)
        const res = await api.get(`/persons/dedup-search?${params.toString()}`)
        // Rank: own-tree matches first, then canonical historical, then KG records.
        // Only top 4 to keep the wizard tight.
        const own = (res.mine || []).map(m => ({ ...m, _bucket: 'tree' }))
        const can = (res.canonical || []).map(m => ({ ...m, _bucket: 'canonical' }))
        const rec = (res.records || []).map(m => ({ ...m, _bucket: 'record' }))
        setMatches([...own, ...can, ...rec].slice(0, 4))
      } catch {
        setMatches([])
      } finally {
        setSearching(false)
      }
    }, 500)
    return () => clearTimeout(debounceRef.current)
  }, [slot.first_name, slot.last_name, slot.birth_year])

  if (!matches.length) return null
  return (
    <div className="onboarding-matches">
      <div className="onboarding-matches__header">
        Possible existing match{matches.length > 1 ? 'es' : ''}{searching ? '…' : ''}
      </div>
      <ul>
        {matches.map(m => (
          <li key={`${m._bucket}-${m.id}`}>
            <button type="button" onClick={() => onPick(m)}>
              <span className="onboarding-matches__name">{formatMatchLabel(m)}</span>
              <span className="onboarding-matches__bucket">{
                m._bucket === 'tree'      ? 'in your trees'
                : m._bucket === 'canonical' ? 'in the archive'
                : 'archive record'
              }</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
